/**
 * SimpleTableView — Shopify-style clean product table for desktop.
 *
 * Designed for "Aha moment" — not just data, but a business dashboard
 * that makes new users feel their store is real and taking shape.
 *
 * Supports expandable sub-rows (e.g., product variants).
 *
 * Used by DocTypeListPageShell when `enableSimpleView` is active.
 */

import { useState, useCallback, Fragment } from "react";
import { cn } from "@/lib/utils";
import { ChevronRight, ChevronDown, ImageIcon } from "lucide-react";

/** Column definition for simple table */
export type SimpleTableColumn<T> = {
  id: string;
  header: string;
  accessor: (row: T) => unknown;
  cell?: (row: T, value: unknown) => React.ReactNode;
  align?: "left" | "center" | "right";
  width?: string;
  hideBelow?: "sm" | "md" | "lg";
};

/** Thumbnail config for the first column */
export type SimpleTableThumbnailConfig = {
  imageFields: string[];
  labelFields: string[];
  size?: number;
};

/** Summary stat for the insight bar */
export type SimpleTableStat = {
  label: string;
  value: number;
  color?: "default" | "green" | "orange" | "red";
  /** When provided, the stat becomes an interactive button (e.g. click to filter) */
  onClick?: () => void;
  /** Renders the stat in an "active" (currently-applied) visual state */
  active?: boolean;
};

type SimpleTableViewProps<T> = {
  items: T[];
  columns: SimpleTableColumn<T>[];
  isLoading: boolean;
  total: number;
  getRowId: (row: T) => string;
  onRowClick?: (id: string, label?: string) => void;
  thumbnail?: SimpleTableThumbnailConfig;
  emptyMessage?: string;
  getSubRows?: (row: T) => T[] | undefined;
  defaultExpanded?: boolean;
  subRowLabel?: string;
  summaryStats?: SimpleTableStat[];
  /** Min row height in px — use to match thumbnail-row height on tables without images */
  minRowHeight?: number;
  /** Custom node rendered when there are no items (overrides emptyMessage) */
  emptyState?: React.ReactNode;
  /** Optional banner rendered above the table (below summary stats) — e.g. a nudge */
  topBanner?: React.ReactNode;
};

function getFieldValue(row: Record<string, unknown>, fields: string[]): string | undefined {
  for (const f of fields) {
    const v = row[f];
    if (typeof v === "string" && v.length > 0) return v;
  }
  return undefined;
}

const HIDE_CLASSES = {
  sm: "hidden sm:table-cell",
  md: "hidden md:table-cell",
  lg: "hidden lg:table-cell",
} as const;

const STAT_NUM_COLORS = {
  default: "text-neutral-900 dark:text-neutral-100",
  green: "text-emerald-700 dark:text-emerald-400",
  orange: "text-amber-700 dark:text-amber-400",
  red: "text-red-700 dark:text-red-400",
} as const;

const STAT_BG_COLORS = {
  default: "bg-neutral-50 dark:bg-neutral-800/60",
  green: "bg-emerald-50 dark:bg-emerald-900/20",
  orange: "bg-amber-50 dark:bg-amber-900/20",
  red: "bg-red-50 dark:bg-red-900/20",
} as const;

const STAT_DOT_COLORS = {
  default: "bg-neutral-300 dark:bg-neutral-600",
  green: "bg-emerald-500 dark:bg-emerald-400",
  orange: "bg-amber-500 dark:bg-amber-400",
  red: "bg-red-500 dark:bg-red-400",
} as const;

export function SimpleTableView<T extends Record<string, unknown>>({
  items,
  columns,
  isLoading,
  total: _total,
  getRowId,
  onRowClick,
  thumbnail,
  emptyMessage = "No items found",
  getSubRows,
  defaultExpanded = false,
  subRowLabel = "variants",
  summaryStats,
  minRowHeight,
  emptyState,
  topBanner,
}: SimpleTableViewProps<T>) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(() => {
    if (!defaultExpanded || !getSubRows) return new Set();
    const initial = new Set<string>();
    for (const item of items) {
      const subs = getSubRows(item);
      if (subs && subs.length > 0) initial.add(getRowId(item));
    }
    return initial;
  });

  const toggleExpand = useCallback((rowId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const hasExpandable = !!getSubRows && items.some((item) => {
    const subs = getSubRows(item);
    return subs && subs.length > 0;
  });

  // Loading skeleton
  if (isLoading && items.length === 0) {
    return (
      <div className="px-2">
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-200 dark:border-neutral-800">
              {thumbnail && <th className="py-2.5 px-3 w-14" />}
              {columns.map((col) => (
                <th
                  key={col.id}
                  className={cn(
                    "py-2.5 px-3 text-[11px] font-medium uppercase tracking-wider text-neutral-400 dark:text-neutral-500 text-left",
                    col.hideBelow && HIDE_CLASSES[col.hideBelow],
                  )}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 8 }).map((_, i) => (
              <tr key={i} className="border-b border-neutral-100 dark:border-neutral-800/50">
                {thumbnail && (
                  <td className="py-3 px-3">
                    <div className="h-11 w-11 rounded-[9px] bg-neutral-100 dark:bg-neutral-800 animate-pulse" />
                  </td>
                )}
                {columns.map((col) => (
                  <td key={col.id} className={cn("py-3 px-3", col.hideBelow && HIDE_CLASSES[col.hideBelow])}>
                    <div className="h-4 w-24 rounded bg-neutral-100 dark:bg-neutral-800 animate-pulse" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (items.length === 0) {
    if (emptyState) return <>{emptyState}</>;
    return (
      <div className="flex items-center justify-center py-20 text-neutral-400 dark:text-neutral-500 text-[13px]">
        {emptyMessage}
      </div>
    );
  }

  const thumbSize = thumbnail?.size ?? 44;

  const renderRow = (item: T, isSubRow: boolean) => {
    const rowId = getRowId(item);
    const imageUrl = thumbnail ? getFieldValue(item, thumbnail.imageFields) : undefined;
    const label = thumbnail ? getFieldValue(item, thumbnail.labelFields) : undefined;
    const subRows = getSubRows?.(item);
    const hasChildren = subRows && subRows.length > 0;
    const isExpanded = expandedRows.has(rowId);
    const rowThumbSize = isSubRow ? Math.round(thumbSize * 0.75) : thumbSize;

    return (
      <tr
        key={rowId}
        onClick={() => onRowClick?.(rowId, label)}
        className={cn(
          "border-b border-neutral-100 dark:border-neutral-800/50 transition-colors",
          onRowClick && "cursor-pointer hover:bg-neutral-50/80 dark:hover:bg-neutral-900/50",
          isSubRow && "bg-neutral-50/60 dark:bg-neutral-900/20",
        )}
        style={minRowHeight ? { height: minRowHeight } : undefined}
      >
        {hasExpandable && (
          <td className="py-2.5 pl-2 pr-0 w-8">
            {hasChildren ? (
              <button
                type="button"
                onClick={(e) => toggleExpand(rowId, e)}
                className="p-1 rounded hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-400 dark:text-neutral-500 transition-colors"
                aria-label={isExpanded ? "Collapse variants" : "Expand variants"}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : isSubRow ? (
              <div className="w-4 h-4 ml-1" />
            ) : null}
          </td>
        )}
        {thumbnail && (
          <td className={cn("py-2 px-3", isSubRow && "pl-6")} style={{ width: thumbSize + 24 }}>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                className="rounded-[9px] object-cover bg-neutral-100 dark:bg-neutral-800"
                style={{ width: rowThumbSize, height: rowThumbSize }}
                loading="lazy"
              />
            ) : (
              <div
                className="rounded-[9px] bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-300 dark:text-neutral-600"
                style={{ width: rowThumbSize, height: rowThumbSize }}
              >
                <ImageIcon className="w-5 h-5" />
              </div>
            )}
          </td>
        )}
        {columns.map((col, colIdx) => {
          const value = col.accessor(item);
          return (
            <td
              key={col.id}
              className={cn(
                "py-1.5 px-3 text-[13px] overflow-hidden",
                col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left",
                col.hideBelow && HIDE_CLASSES[col.hideBelow],
              )}
            >
              {col.cell ? col.cell(item, value) : (
                <span className={cn(
                  "truncate block",
                  isSubRow
                    ? "text-neutral-400 dark:text-neutral-500"
                    : "text-neutral-600 dark:text-neutral-400",
                )}>
                  {value != null && value !== "" ? String(value) : <span className="text-neutral-300 dark:text-neutral-700">{"\u2014"}</span>}
                </span>
              )}
              {colIdx === 0 && hasChildren && !isSubRow && (
                <span className="text-[11px] text-neutral-400 dark:text-neutral-500">
                  {subRows.length} {subRowLabel}
                </span>
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div className="px-2">
      {/* Summary insight bar */}
      {summaryStats && summaryStats.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-3 mb-1">
          {summaryStats.map((stat, i) => {
            const c = stat.color ?? "default";
            const interactive = !!stat.onClick;
            const content = (
              <>
                <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", STAT_DOT_COLORS[c])} />
                <span className={cn("text-[15px] font-semibold tabular-nums", STAT_NUM_COLORS[c])}>
                  {stat.value}
                </span>
                <span className="text-[12px] text-neutral-500 dark:text-neutral-400">
                  {stat.label}
                </span>
              </>
            );
            const base = cn(
              "flex items-center gap-2 px-3 py-1.5 rounded-lg",
              STAT_BG_COLORS[c],
            );
            if (interactive) {
              return (
                <button
                  key={i}
                  type="button"
                  onClick={stat.onClick}
                  aria-pressed={stat.active}
                  className={cn(
                    base,
                    "transition-all cursor-pointer hover:brightness-95 dark:hover:brightness-110",
                    stat.active
                      ? "ring-2 ring-offset-1 ring-amber-400 dark:ring-amber-500 dark:ring-offset-neutral-950"
                      : "ring-0",
                  )}
                >
                  {content}
                </button>
              );
            }
            return (
              <div key={i} className={base}>
                {content}
              </div>
            );
          })}
        </div>
      )}

      {/* Nudge / banner (below stats, above table) */}
      {topBanner}

      <div className="overflow-x-auto">
      <table className="w-full" style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr className="border-b border-neutral-200 dark:border-neutral-800">
            {hasExpandable && <th className="py-2.5 pl-2 pr-0 w-8" />}
            {thumbnail && <th className="py-2.5 px-3" style={{ width: thumbSize + 24 }} />}
            {columns.map((col) => (
              <th
                key={col.id}
                className={cn(
                  "py-2.5 px-3 text-[11px] font-medium uppercase tracking-wider text-neutral-400 dark:text-neutral-500 whitespace-nowrap",
                  col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left",
                  col.hideBelow && HIDE_CLASSES[col.hideBelow],
                )}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const rowId = getRowId(item);
            const subRows = getSubRows?.(item);
            const hasChildren = subRows && subRows.length > 0;
            const isExpanded = expandedRows.has(rowId);

            return (
              <Fragment key={rowId}>
                {renderRow(item, false)}
                {hasChildren && isExpanded && subRows.map((child) => renderRow(child, true))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}
